import { renderToString } from 'react-dom/server';
import {
  COLOR_TEXT_DARK,
  COLOR_NEUTRAL_WHITE,
  FONT_SIZE_100,
  SIZE_ICON_LG,
  SIZE_ICON_MD,
  SIZE_ICON_XS,
  SPACE_100,
  SPACE_300
} from '@alltrails/denali/tokens';
import { isFirefox, isIos, isSafari } from '@alltrails/core';
import type { Size } from '@alltrails/denali/types';
import { IconComponent } from '@alltrails/denali/components/Icon';
import IconRenderer from '@alltrails/denali/components/IconRenderer';
import manropeNumbersBase64 from '../manropeNumbersBase64';
import { SvgDefinition } from '../useSvgImages';
import PopupOffset from '../../types/PopupOffset';

export const circleShadowId = 'circle-shadow';
export const circleShadowDef = `<radialGradient id="${circleShadowId}">
  <stop stop-color="#000000" stop-opacity="0.3"/>
  <stop offset="70%" stop-color="#000000" stop-opacity="0.3"/>
  <stop offset="100%" stop-color="#000000" stop-opacity="0"/>
</radialGradient>`;

const circleMarkerBorderWidth = 2;
const circleMarkerShadowWidth = 2;
export const getCircleMarkerSizeWithoutShadow = (innerDiameter: number) => innerDiameter + 2 * circleMarkerBorderWidth;
export const getCircleMarkerSize = (innerDiameter: number) => innerDiameter + 2 * (circleMarkerBorderWidth + circleMarkerShadowWidth);

type CreateCircleMarkerArgs = {
  imageName: string;
  additionalDefs?: string;
  borderFill?: string;
  innerDiameter: number;
  innerFill: string;
};
export const createCircleMarker = ({
  imageName,
  additionalDefs,
  borderFill = COLOR_NEUTRAL_WHITE,
  innerDiameter,
  innerFill
}: CreateCircleMarkerArgs): SvgDefinition => {
  const borderWidth = circleMarkerBorderWidth;
  const shadowWidth = circleMarkerShadowWidth;
  const size = getCircleMarkerSize(innerDiameter);
  const halfSize = size / 2;

  const svgString = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      ${circleShadowDef}
      ${additionalDefs}
    </defs>
    <circle cx="${halfSize}" cy="${halfSize}" r="${halfSize}" fill="url(#${circleShadowId})" />
    <circle cx="${halfSize}" cy="${halfSize}" r="${halfSize - shadowWidth}" fill="${borderFill}" />
    <circle cx="${halfSize}" cy="${halfSize}" r="${halfSize - shadowWidth - borderWidth}" fill="${innerFill}"  />
  </svg>`;

  return { name: imageName, svgString, height: size, width: size };
};

// Keep consistent with MapCenterMarker in explore app
export const createTargetMarker = (imageName: string, color: string): SvgDefinition => {
  const svgString = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      ${circleShadowDef}
    </defs>
    <circle cx="12" cy="12" r="12" fill="url(#${circleShadowId})" />
    <circle cx="12" cy="12" r="10" fill="${COLOR_TEXT_DARK}" />
    <circle cx="12" cy="12" r="7" fill="${color}"  />
    <circle cx="12" cy="12" r="3" fill="${COLOR_TEXT_DARK}"  />
  </svg>`;

  return { name: imageName, svgString, height: targetSize, width: targetSize };
};
export const targetSize = 24;

type CreatePinMarkerArgs = {
  imageName: string;
  borderFill?: string;
  innerFill: string;
  numberContent?: number;
  renderInnerDot?: boolean;
  renderInnerDash?: boolean;
  dashFill?: string;
  textFill?: string;
  renderInnerBookmark?: boolean;
  renderCheckmark?: boolean;
  isCollapsed?: boolean;
};
export const createPinMarker = ({
  imageName,
  borderFill = COLOR_NEUTRAL_WHITE,
  innerFill,
  numberContent,
  renderInnerDot,
  renderInnerDash,
  dashFill = COLOR_NEUTRAL_WHITE,
  textFill,
  renderInnerBookmark = false,
  renderCheckmark = false,
  isCollapsed = false
}: CreatePinMarkerArgs): SvgDefinition => {
  const safari = isSafari();
  const dashSymbol = renderInnerDash
    ? `<path d="M18.75 11.25L11.25 18.75" stroke="${dashFill}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
    : '';
  const bookmark = renderInnerBookmark
    ? `<path transform="translate(8, 8.5)" d="M4.08366 1.3125C3.1977 1.3125 2.47949 2.03071 2.47949 2.91667V12.25C2.47949 12.406 2.56251 12.5501 2.6974 12.6284C2.83229 12.7067 2.99865 12.7072 3.13405 12.6299L7.00033 10.4206L10.8666 12.6299C11.002 12.7072 11.1684 12.7067 11.3033 12.6284C11.4381 12.5501 11.5212 12.406 11.5212 12.25V2.91667C11.5212 2.03071 10.8029 1.3125 9.91699 1.3125H4.08366Z" fill="${borderFill}"/>`
    : '';
  const checkmark = renderCheckmark
    ? `<path transform="translate(8, 8.5)" d="M12 4L5 11L2 7.75" stroke="${borderFill}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`
    : '';
  const dot = renderInnerDot ? `<circle cx="15" cy="15.25" r="3" fill="${borderFill}"/>` : '';

  // Safari inconsistently renders the text when we try to load/apply Manrope, so fallback to system-ui
  const text = numberContent
    ? `<text fill="${textFill || borderFill}" font-family="${safari ? 'system-ui' : 'Manrope Numbers'}" font-size="${FONT_SIZE_100}px" font-weight="normal" text-anchor="middle" dominant-baseline="middle" x="15" y="16.5">${numberContent}</text>`
    : '';
  const styleDef = safari
    ? ''
    : `<style>
        @font-face {
          font-family: 'Manrope Numbers';
          src: url(data:font/truetype;charset=utf-8;base64,${manropeNumbersBase64}) format('truetype');
        }
      </style>`;

  // Safari makes everything blurry when we try to render this shadow, so just omit it on safari
  const dropShadowDef = safari
    ? ''
    : `<filter id="pin-drop-shadow" x="-0.25" y="-0.25" width="30.5" height="33.5825" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset/>
        <feGaussianBlur stdDeviation="0.5"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="blend-result"/>
        <feBlend mode="normal" in="SourceGraphic" in2="blend-result" result="shape"/>
      </filter>`;

  if (isCollapsed) {
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
      <g transform="translate(3, -10)">
        <g filter="url(#filter0_dd_251_40133)">
          <path d="M18 28C18 29.6569 16.6569 31 15 31C13.3431 31 12 29.6569 12 28C12 26.3431 13.3431 25 15 25C16.6569 25 18 26.3431 18 28Z" fill="#1B2818"/>
          <path d="M15 24.25C17.0711 24.25 18.75 25.9289 18.75 28C18.75 30.0711 17.0711 31.75 15 31.75C12.9289 31.75 11.25 30.0711 11.25 28C11.25 25.9289 12.9289 24.25 15 24.25Z" stroke="white" stroke-width="1.5"/>
        </g>
      </g>
      <defs>
        <filter id="filter0_dd_251_40133" x="6.5" y="21.5" width="17" height="17" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dy="2"/>
          <feGaussianBlur stdDeviation="2"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_251_40133"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset/>
          <feGaussianBlur stdDeviation="0.5"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"/>
          <feBlend mode="normal" in2="effect1_dropShadow_251_40133" result="effect2_dropShadow_251_40133"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_251_40133" result="shape"/>
        </filter>
      </defs>
    </svg>`;

    return { name: imageName, svgString, height: pinMarkerCollapsedHeight, width: pinMarkerCollapsedWidth };
  }

  const svgString = `<svg width="30" height="36" viewBox="0 0 30 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      ${styleDef}
      <radialGradient id="pin-shadow" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(15 32) rotate(90) scale(3.5 10.5)">
        <stop stop-opacity="0.24"/>
        <stop offset="1" stop-color="#8F8F8F" stop-opacity="0"/>
      </radialGradient>
      ${dropShadowDef}
    </defs>
    <path d="M27 32C27 34.2091 21.6274 36 15 36C8.37258 36 3 34.2091 3 32C3 29.7909 8.37258 28 15 28C21.6274 28 27 29.7909 27 32Z" fill="url(#pin-shadow)"/>
    <g ${safari ? '' : 'filter="url(#pin-drop-shadow)"'}>
      <path d="M28.25 15C28.25 17.784 27.3914 20.3674 25.9245 22.5C24.0682 25.1988 18.4997 29.2892 16.1096 30.9785C15.4419 31.4504 14.5581 31.4504 13.8904 30.9785C11.5003 29.2892 5.93184 25.1988 4.07547 22.5C2.60861 20.3674 1.75 17.784 1.75 15C1.75 7.68223 7.68223 1.75 15 1.75C22.3178 1.75 28.25 7.68223 28.25 15Z" fill="${innerFill}" stroke="${borderFill}" stroke-width="2"/>
    </g>
    ${text || dashSymbol || bookmark || checkmark || dot}
  </svg>`;

  return { name: imageName, svgString, height: pinMarkerHeight, width: pinMarkerWidth };
};
export const pinMarkerHeight = 36;
export const pinMarkerWidth = 30;
export const pinMarkerVerticalOffset = 4; // 4 px from the bottom of the shadow to the bottom of the actual pin, which we want to position at the lat/lng
export const pinMarkerCollapsedHeight = 36;
export const pinMarkerCollapsedWidth = 36;
export const pinMarkerCollapsedVerticalOffset = pinMarkerCollapsedHeight / 2 - pinMarkerVerticalOffset;

export const createParkPinSvg = (imageName: string, color: string): SvgDefinition => {
  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
      <g filter="url(#filter0_dd_769_214)">
        <circle cx="18" cy="16" r="12" fill="${color}"/>
        <circle cx="18" cy="16" r="13" stroke="white" stroke-width="2"/>
      </g>
      <path d="M19.7071 13.4397C19.7071 13.4397 20.9281 14.5776 21.983 14.5776C22.5519 14.5776 23.1209 13.9717 23.1209 13.4397C23.1209 12.1157 19.7283 10.9323 18.7303 8.2563C18.6167 7.95169 18.3253 7.75 18.0002 7.75C17.6751 7.75 17.3838 7.95169 17.2702 8.2563C16.2721 10.9323 12.8795 12.1157 12.8795 13.4397C12.8795 13.9717 13.4485 14.5776 14.0174 14.5776C15.0723 14.5776 16.2933 13.4397 16.2933 13.4397C16.2933 16.8534 12.3105 17.4224 12.3105 19.1293C12.3105 19.7645 12.8739 20.2672 13.4485 20.2672C14.1113 20.2672 14.8114 19.8629 15.4004 19.4353C15.8678 19.0959 16.5231 19.4379 16.5231 20.0155L16.5231 24.25H19.4773L19.4773 20.0155C19.4773 19.4379 20.1326 19.0959 20.6 19.4353C21.189 19.8629 21.8891 20.2672 22.5519 20.2672C23.1265 20.2672 23.6899 19.7645 23.6899 19.1293C23.6899 17.4224 19.7071 16.8534 19.7071 13.4397Z" fill="white"/>
      <defs>
        <filter id="filter0_dd_769_214" x="0" y="0" width="36" height="36" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dy="2"/>
          <feGaussianBlur stdDeviation="2"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_769_214"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset/>
          <feGaussianBlur stdDeviation="0.5"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"/>
          <feBlend mode="normal" in2="effect1_dropShadow_769_214" result="effect2_dropShadow_769_214"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_769_214" result="shape"/>
        </filter>
      </defs>
    </svg>
    `;

  return { name: imageName, svgString, height: 36, width: 36 };
};
export const parkPinVerticalOffset = 4;

export type POIPinSize = Size<'xs' | 'md' | 'lg'>;
export const poiPinHeightXs = 36;
export const poiPinIconWidthXs = 32;
export const poiPinImageWidthXs = 36;
export const poiPinVerticalOffset = 3.5;
const colorAreaDiameterXs = 24;

export const poiPinHeightMd = 62;
export const poiPinIconWidthMd = 62;
export const poiPinImageWidthMd = 62;
const colorAreaDiameterMd = 48;

export const poiPinHeightLg = 86;
export const poiPinIconWidthLg = 86;
export const poiPinImageWidthLg = 86;
const colorAreaDiameterLg = 72;

// Safari makes everything blurry when we try to render this shadow, so just omit it on safari
const poiDropShadowDef = (size: POIPinSize) => {
  const dims = size === 'lg' ? poiPinHeightLg : size === 'md' ? poiPinHeightMd : poiPinHeightXs;
  return `
    <filter id="poi-pin-shadow-filter" x="-2" y="1" width="${dims}" height="${dims}" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dy="2"/>
      <feGaussianBlur stdDeviation="2"/>
      <feComposite in2="hardAlpha" operator="out"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="blend1-result"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset/>
      <feGaussianBlur stdDeviation="0.5"/>
      <feComposite in2="hardAlpha" operator="out"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"/>
      <feBlend mode="normal" in2="blend1-result" result="blend2-result"/>
      <feBlend mode="normal" in="SourceGraphic" in2="blend2-result" result="shape"/>
    </filter>
  `;
};

export const createIconPinSvg = (imageName: string, icon: IconComponent, size: POIPinSize, fill): SvgDefinition => {
  const safari = isSafari();
  const isIosFirefox = isFirefox() && isIos();
  const preventShadow = safari || isIosFirefox;
  const width = size === 'lg' ? poiPinIconWidthLg : size === 'md' ? poiPinIconWidthMd : poiPinIconWidthXs;
  const height = size === 'lg' ? poiPinHeightLg : size === 'md' ? poiPinHeightMd : poiPinHeightXs;
  const circleHeight = height - 3;
  const cx = width / 2;
  const cy = Math.round(circleHeight / 2);
  const colorAreaDiameter = size === 'lg' ? colorAreaDiameterLg : size === 'md' ? colorAreaDiameterMd : colorAreaDiameterXs;
  const borderWidth = size === 'lg' ? 3 : size === 'md' ? 2.5 : 2;
  const pointTranslation = size === 'lg' ? 'translate(38 79)' : size === 'md' ? 'translate(26 55)' : 'translate(11 30)';
  const iconString = renderToString(
    <IconRenderer
      icon={{ Component: icon }}
      defaults={{
        svgAttributes: {
          fill: COLOR_NEUTRAL_WHITE,
          x: size === 'lg' ? '26.5' : size === 'md' ? '19' : '8',
          y: size === 'lg' ? '26' : size === 'md' ? '18' : '9',
          width: String(size === 'lg' ? SIZE_ICON_LG : size === 'md' ? SIZE_ICON_MD : SIZE_ICON_XS),
          height: String(size === 'lg' ? SIZE_ICON_LG : size === 'md' ? SIZE_ICON_MD : SIZE_ICON_XS)
        }
      }}
    />
  );
  const svgString = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="${cx}" cy="${circleHeight}" rx="12" ry="4" fill="url(#poi-icon-radial-gradient)"/>
      <g ${preventShadow ? '' : 'filter="url(#poi-pin-shadow-filter)"'}>
        <circle cx="${cx}" cy="${cy}" r="${colorAreaDiameter / 2}" fill="${fill}"/>
        <circle cx="${cx}" cy="${cy}" r="${(colorAreaDiameter + borderWidth) / 2}" stroke="white" stroke-width="${borderWidth}"/>
      </g>
      <path d="M0 0L3.44613 3.81261C3.86272 4.27087 4.43136 4.5 5 4.5C5.56864 4.5 6.13728 4.27087 6.55387 3.81261L10 0H0Z" fill="white" transform="${pointTranslation}"/>
      ${iconString}
      <defs>
        ${preventShadow ? '' : poiDropShadowDef(size)}
        <radialGradient id="poi-icon-radial-gradient" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(${cx} ${circleHeight}) rotate(90) scale(3.5 10.5)">
          <stop stop-opacity="0.24"/>
          <stop offset="1" stop-color="#8F8F8F" stop-opacity="0"/>
        </radialGradient>
      </defs>
    </svg>
  `;

  return { name: imageName, svgString, height, width };
};

export const createPhotoPinSvg = (imageName: string, id: number, profileImageUrl: string, size: POIPinSize): SvgDefinition => {
  // Safari & Firefox on IOS doesn't handle these image pins well, so double the size of the SVG for better resolution
  const safari = isSafari();
  const isIosFirefox = isFirefox() && isIos();
  const shouldScalePin = safari || isIosFirefox;
  let width = size === 'lg' ? poiPinImageWidthLg : size === 'md' ? poiPinImageWidthMd : poiPinImageWidthXs;
  let height = size === 'lg' ? poiPinHeightLg : size === 'md' ? poiPinHeightMd : poiPinHeightXs;
  const originalWidth = width;
  const originalHeight = height;
  let circleHeight = height - 3 * (shouldScalePin ? 2 : 1);
  let cx = width / 2;
  let cy = Math.round(circleHeight / 2);
  let colorAreaDiameter = size === 'lg' ? colorAreaDiameterLg : size === 'md' ? colorAreaDiameterMd : colorAreaDiameterXs;
  let borderWidth = size === 'lg' ? 3 : size === 'md' ? 2.5 : 2;
  if (shouldScalePin) {
    width *= 2;
    height *= 2;
    circleHeight *= 2;
    cx *= 2;
    cy *= 2;
    colorAreaDiameter *= 2;
    borderWidth *= 2;
  }
  const pointTranslation = size === 'lg' ? 'translate(38 79)' : size === 'md' ? 'translate(26 55)' : 'translate(13 29)';
  const scaledPointTranslation =
    size === 'lg' ? 'translate(76 154) scale(2)' : size === 'md' ? 'translate(52 106) scale(2)' : 'translate(26 54) scale(2)';
  const svgString = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <ellipse cx="${cx}" cy="${circleHeight}" rx="${shouldScalePin ? 24 : 12}" ry="${shouldScalePin ? 8 : 4}" fill="url(#poi-image-radial-gradient)"/>
      <g ${shouldScalePin ? '' : 'filter="url(#poi-pin-shadow-filter)"'}>
        <circle cx="${cx}" cy="${cy}" r="${colorAreaDiameter / 2}" fill="url(#poi-pattern-${id})"/>
        <circle cx="${cx}" cy="${cy}" r="${(colorAreaDiameter + borderWidth) / 2}" stroke="white" stroke-width="${borderWidth}"/>
      </g>
      <path d="M0 0L3.44613 3.81261C3.86272 4.27087 4.43136 4.5 5 4.5C5.56864 4.5 6.13728 4.27087 6.55387 3.81261L10 0H0Z" fill="white" transform="${shouldScalePin ? scaledPointTranslation : pointTranslation}"/>
      <defs>
        ${shouldScalePin ? '' : poiDropShadowDef(size)}
        <pattern id="poi-pattern-${id}" patternUnits="objectBoundingBox" width="1" height="1">
          <use href="#poi-image-${id}" xlink:href="#poi-image-${id}"/>
        </pattern>
        <radialGradient id="poi-image-radial-gradient" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(${cx} ${circleHeight}) rotate(90) ${shouldScalePin ? 'scale(7 21)' : 'scale(3.5 10.5)'}">
          <stop stop-opacity="0.24"/>
          <stop offset="1" stop-color="#8F8F8F" stop-opacity="0"/>
        </radialGradient>
        <image id="poi-image-${id}" width="${colorAreaDiameter}" height="${colorAreaDiameter}" href="${profileImageUrl}" xlink:href="${profileImageUrl}"/>
      </defs>
    </svg>
  `;

  return { name: imageName, svgString, height: originalHeight, width: originalWidth };
};

export const pinPopupOffset: PopupOffset = {
  bottom: [0, pinMarkerVerticalOffset - pinMarkerHeight - SPACE_100],
  'bottom-left': [pinMarkerWidth / -2, pinMarkerVerticalOffset - pinMarkerHeight - SPACE_100],
  'bottom-right': [pinMarkerWidth / 2, pinMarkerVerticalOffset - pinMarkerHeight - SPACE_100],
  top: [0, SPACE_100 + pinMarkerVerticalOffset],
  'top-left': [pinMarkerWidth / -2, SPACE_100 + pinMarkerVerticalOffset],
  'top-right': [pinMarkerWidth / 2, SPACE_100 + pinMarkerVerticalOffset],
  left: [pinMarkerWidth / 2 + SPACE_100, (pinMarkerVerticalOffset - pinMarkerHeight) / 2],
  right: [pinMarkerWidth / -2 - SPACE_100, (pinMarkerVerticalOffset - pinMarkerHeight) / 2]
};

export const clusterPopupOffset: PopupOffset = {
  bottom: [0, -SPACE_300],
  top: [0, SPACE_300],
  'bottom-left': [pinMarkerWidth / -2, -SPACE_300],
  'bottom-right': [pinMarkerWidth / 2, -SPACE_300],
  'top-left': [pinMarkerWidth / -2, SPACE_300],
  'top-right': [pinMarkerWidth / 2, SPACE_300],
  left: [pinMarkerWidth / 2 + SPACE_100, (pinMarkerVerticalOffset - pinMarkerHeight) / 2],
  right: [pinMarkerWidth / -2 - SPACE_100, (pinMarkerVerticalOffset - pinMarkerHeight) / 2]
};
